/**
 * FILE: apps\hub\src\hooks\useConfirm.ts
 * PURPOSE: Custom React hook.
 * AGENT_CONTEXT: Reusable logic. Follow Rules of Hooks. No side effects in render.
 * LAST_REVIEWED: 2026-05-07
 */

/**
 * FILE: apps/hub/src/hooks/useConfirm.ts
 * PURPOSE: Imperative confirmation dialog helper for destructive flows.
 */

import { useCallback, useState } from 'react'

export type ConfirmOptions = {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'danger'
}

type Pending = ConfirmOptions & {
  resolve: (ok: boolean) => void
}

export function useConfirm() {
  const [pending, setPending] = useState<Pending | null>(null)

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...opts, resolve })
      }),
    []
  )

  const confirmDialogProps = pending
    ? {
        open: true as const,
        title: pending.title,
        description: pending.description,
        confirmLabel: pending.confirmLabel,
        cancelLabel: pending.cancelLabel,
        variant: pending.variant,
        onConfirm: () => {
          pending.resolve(true)
          setPending(null)
        },
        onCancel: () => {
          pending.resolve(false)
          setPending(null)
        },
      }
    : null

  return { confirm, confirmDialogProps }
}
